import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { AuthService } from "../services/api/auth/auth.service";
import { catchError, EMPTY, Observable, throwError } from "rxjs";
import { SnackbarService } from "../services/snackbar/snackbar.service";
import { ModalService } from "../services/modal/modal.service";
import { SILENT_HTTP } from "../services/api/telemetry/telemetry.service";

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Inject AuthService and Router
  const authService = inject(AuthService);

  // List of URLs that do not require token
  const nonAuthUrls = ["/login", "/logout", "/signup"];
  // Check if the request URL is in the list of non-auth URLs
  const isNonAuthUrls = nonAuthUrls.some((url) => req.url.includes(url));
  // not the in the nonAuthUrls list, should add token
  if (!isNonAuthUrls) {
    const token = authService.getToken();
    if (token) {
      req = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Silent requests (e.g. fire-and-forget telemetry) never trigger logout;
      // the user's "real" requests will trip the same 401 and handle it.
      if (req.context.get(SILENT_HTTP)) {
        return throwError(() => error);
      }
      if (
        error.status === 401 &&
        "state" in error.error &&
        error.error.state === "invalid_or_expired_token"
      ) {
        authService.logout();
        return EMPTY;
      }
      return throwError(() => error);
    }),
  );
};

export const redirectDemoUserInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const isDemo = authService.internalModeValue === "demo";
  if (
    !isDemo ||
    (!req.url.startsWith("http") && !req.url.startsWith("https"))
  ) {
    return next(req);
  }
  const newUrl = new URL(req.url);
  const pastEndpoints = [
    "/clients",
    "/fields",
    "/fieldsnoet",
    "/claimed-fields",
    "/claimed-fields-manager",
    "/manager-statistics",
  ];
  // Check if the request URL matches any of the past endpoints
  if (!pastEndpoints.some((endpoint) => newUrl.pathname.startsWith(endpoint))) {
    return next(req);
  }
  newUrl.pathname = `/past${newUrl.pathname}`;
  const newReq = req.clone({
    url: newUrl.toString(),
  });
  return next(newReq);
};

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const snackService = inject(SnackbarService);
  const modalService = inject(ModalService);
  const isDemo = inject(AuthService).internalModeValue === "demo";

  // Error states that should only show inline (no toast)
  const inlineOnlyStates = [
    "account_locked",
    "credential_error",
    "twofa_locked",
    "signup_locked",
    "invalid_invitation_code",
    "invitation_email_mismatch",
    "signin_code_invalid",
    "not_valid_entity_email",
  ];

  // Silent requests skip all user-facing error UX (snackbars, dialogs).
  if (req.context.get(SILENT_HTTP)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check both error.state (after authInterceptor transforms it) and error.error?.state (original)
      const errorState = error.error?.state || (error as any).state;

      // Skip toast for errors that are shown inline
      if (errorState && inlineOnlyStates.includes(errorState)) {
        return throwError(() => error.error);
      }

      const errorMessage = error.error?.message || (error as any).message;
      if (error.status === 404 && isDemo) {
        snackService.show({
          message: `Demo Mode does not allow this operation. ${errorMessage}`,
        });
      } else if (error.status === 409) {
        // Conflict - probably when user trying to create a resource that already exists
        modalService.openDialogAsync({
          title: "Conflict Error",
          message: errorMessage || "A conflict occurred with your request.",
        });
      } else if (errorMessage) {
        snackService.show({
          message: `Error: ${errorMessage}`,
        });
      }
      return throwError(() => error);
    }),
  );
};
